import {Box, Slider, TextField} from '@mui/material';
import React from 'react';

import {fromDisplaySpeed, toDisplaySpeed} from '../../constants/common';
import {useAppSelector} from '../../hooks/redux';

interface SpeedFilterSliderProps {
  min?: number;
  max?: number;
  lowerValue: number;
  upperValue: number;
  onLowerChange: (value: number) => void;
  onUpperChange: (value: number) => void;
}

const SpeedFilterSlider: React.FC<SpeedFilterSliderProps> = ({
  min = 0,
  max = 100,
  lowerValue,
  upperValue,
  onLowerChange,
  onUpperChange,
}) => {
  const speedFormat = useAppSelector(({app}) => app.speedFormat);

  // Props carry KPH values (matching backend). Convert to the display unit for UI.
  const displayMin = toDisplaySpeed(min, speedFormat);
  const displayMax = toDisplaySpeed(max, speedFormat);
  const displayLower = toDisplaySpeed(lowerValue, speedFormat);
  const displayUpper = toDisplaySpeed(upperValue, speedFormat);

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const [newLower, newUpper] = newValue;
      // Convert display values back to KPH before calling parent callbacks.
      if (newLower !== displayLower) {
        onLowerChange(fromDisplaySpeed(newLower, speedFormat));
      }
      if (newUpper !== displayUpper) {
        onUpperChange(fromDisplaySpeed(newUpper, speedFormat));
      }
    }
  };

  const handleLowerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!isNaN(value) && value >= displayMin && value <= displayUpper) {
      onLowerChange(fromDisplaySpeed(value, speedFormat));
    }
  };

  const handleUpperInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!isNaN(value) && value <= displayMax && value >= displayLower) {
      onUpperChange(fromDisplaySpeed(value, speedFormat));
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={2} width="100%">
      <TextField
        type="number"
        label={`${speedFormat.toUpperCase()} Min`}
        value={displayLower}
        onChange={handleLowerInputChange}
        inputProps={{min: displayMin, max: displayUpper, step: 10}}
        size="small"
      />

      <Box flex={1} minWidth={200}>
        <Slider
          value={[displayLower, displayUpper]}
          onChange={handleSliderChange}
          min={displayMin}
          max={displayMax}
          step={10}
          disableSwap
        />
      </Box>

      <TextField
        type="number"
        label={`${speedFormat.toUpperCase()} Max`}
        value={displayUpper}
        onChange={handleUpperInputChange}
        inputProps={{min: displayLower, max: displayMax, step: 10}}
        size="small"
      />
    </Box>
  );
};

export default SpeedFilterSlider;
